/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  forwardRef,
  useCallback,
  useRef,
  type ForwardedRef,
  type RefObject,
} from "react";
import { mergeProps, useCheckbox } from "react-aria";
import { useToggleState } from "react-stately";

import { cssFns, type Style } from "@superweb/css";

import { useUiColors, useUiShadows } from "../../theme";
import { CheckboxInternal } from "../../checkbox-internal";
import type { SelectionState } from "../table";
import { useBaseCellStyle, zIndexMap } from "./cells";
import { Tooltip, useTooltip } from "../../tooltip";

export const CheckboxHeaderCell = forwardRef(
  (
    {
      index,
      stickyShift,
      tabIndex,
      selection,
      onChange,
      inputRef,
      variant = "fill",
      isStickyFirstColumn,
      stickyFirstColumnShift,
      progress = false,
    }: {
      index: number;
      stickyShift?: string;
      tabIndex?: number;
      selection: SelectionState;
      onChange: (state: SelectionState) => void;
      inputRef: RefObject<HTMLInputElement>;
      variant?: "fill" | "ghost";
      isStickyFirstColumn?: boolean;
      stickyFirstColumnShift?: number;
      progress?: boolean;
    },
    ref: ForwardedRef<HTMLTableCellElement>,
  ) => {
    const uiColors = useUiColors();

    const style: Style = {
      ...useBaseCellStyle({
        isFocusVisible: false,
        isHeaderCell: true,
        variant,
      }),
      paddingBlockStart: "12px",
      paddingBlockEnd: "12px",
      paddingInlineStart: "16px",
      paddingInlineEnd: "8px",
      zIndex:
        index === 1 ? zIndexMap.headerFirstColumn : zIndexMap.headerColumn,
      position: "sticky",
      top: stickyShift || "0",
      ...(isStickyFirstColumn && {
        insetInlineStart: `${stickyFirstColumnShift}px`,
      }),

      ...(variant === "ghost"
        ? {
            backgroundColor: uiColors.backgroundMinor,
            minHeight: "56px",
          }
        : {
            backgroundColor: uiColors.background,
          }),
    };

    const onHeaderCheckboxValueChange = useCallback(
      (isSelected: boolean) => {
        if (progress) {
          return;
        }
        onChange(
          isSelected
            ? {
                all: true,
                exclude: new Set<string>(),
              }
            : {
                all: false,
                include: new Set<string>(),
              },
        );
      },
      [onChange, progress],
    );

    const toggleHeaderCheckboxState = useToggleState({
      isSelected: selection.all,
      onChange: onHeaderCheckboxValueChange,
    });

    const indeterminate = selection.all && selection.exclude.size !== 0;

    const { inputProps } = useCheckbox(
      {
        isSelected: selection.all,
        onChange: onHeaderCheckboxValueChange,
        "aria-label": "Select all",
        isIndeterminate: indeterminate,
      },
      toggleHeaderCheckboxState,
      inputRef,
    );

    return (
      <th css={style} aria-colindex={index} ref={ref}>
        <div css={{ opacity: progress ? "0.5" : undefined }}>
          <CheckboxInternal
            value={selection.all}
            indeterminate={indeterminate}
            inputProps={{
              ...inputProps,
              "aria-checked": indeterminate
                ? "mixed"
                : selection.all
                  ? "true"
                  : "false",
              "aria-busy": progress,
              tabIndex,
            }}
            inputRef={inputRef}
          />
        </div>
      </th>
    );
  },
);

export const CheckboxCell = forwardRef(
  (
    {
      checkboxKey,
      disabled,
      selection,
      onChange,
      inputRef,
      tabIndex,
      isHovered = false,
      isStickyFirstColumn = false,
      index,
      variant = "fill",
      position,
      isTopPinned,
      isBottomPinned,
      stickyFirstColumnShift = 0,
      isLastRow = false,
      progress = false,
    }: {
      checkboxKey: string;
      disabled?: boolean | string;
      selection: SelectionState;
      onChange: (state: SelectionState) => void;
      inputRef: RefObject<HTMLInputElement>;
      tabIndex: number;
      isHovered?: boolean;
      isStickyFirstColumn?: boolean;
      index: number;
      variant?: "fill" | "ghost";
      isTopPinned?: boolean;
      isBottomPinned?: boolean;
      stickyFirstColumnShift?: number;
      isLastRow?: boolean;
      position: {
        firstLeft?: boolean;
        lastLeft?: boolean;
      };
      progress?: boolean;
    },
    ref: ForwardedRef<HTMLTableCellElement>,
  ) => {
    const uiColors = useUiColors();
    const uiShadows = useUiShadows();

    const isPinned = isTopPinned || isBottomPinned;

    const value =
      !disabled &&
      (selection.all
        ? !selection.exclude.has(checkboxKey)
        : selection.include.has(checkboxKey));

    const onValueChange = useCallback(
      (isSelected: boolean) => {
        if (disabled || progress) {
          return;
        }

        let res;
        if (selection.all) {
          res = new Set<string>(selection.exclude);
          if (isSelected) {
            res.delete(checkboxKey);
          } else {
            res.add(checkboxKey);
          }
        } else {
          res = new Set<string>(selection.include);
          if (isSelected) {
            res.add(checkboxKey);
          } else {
            res.delete(checkboxKey);
          }
        }
        onChange(
          selection.all
            ? { all: selection.all, exclude: res }
            : {
                all: selection.all,
                include: res,
              },
        );
      },
      [checkboxKey, selection, disabled, progress, onChange],
    );

    const toggleState = useToggleState({
      isSelected: value,
      onChange: onValueChange,
    });

    const { inputProps } = useCheckbox(
      {
        isSelected: value,
        onChange: onValueChange,
        "aria-label": "Select",
      },
      toggleState,
      inputRef,
    );

    const targetTooltipRef = useRef<HTMLDivElement>(null);

    const {
      triggerProps,
      tooltipProps,
      state: tooltipState,
    } = useTooltip(targetTooltipRef);

    return (
      <td
        width={56}
        css={{
          ...useBaseCellStyle({
            isFocusVisible: false,
            isHeaderCell: false,
            ...(variant === "ghost" && { position }),
            variant,
            isPinned,
          }),
          paddingBlockStart: "12px",
          paddingBlockEnd: "12px",
          paddingInlineStart: "16px",
          paddingInlineEnd: "8px",
          ...(isTopPinned &&
            !isStickyFirstColumn && {
              zIndex: zIndexMap.stickyRow,
              boxShadow: uiShadows.bottomNormal,
              clipPath: `inset(0 0 -40px 0);`,
            }),
          ...(isBottomPinned &&
            !isStickyFirstColumn && {
              zIndex: zIndexMap.stickyRow,
              boxShadow: uiShadows.topNormal,
              clipPath: `inset(-40px 0 0 0);`,
            }),

          ...(isStickyFirstColumn && {
            position: "sticky",
            insetInlineStart: `${stickyFirstColumnShift}px`,
            zIndex: zIndexMap.stickyFirstColumn,
          }),

          ...(variant === "ghost" &&
            !isPinned &&
            !isLastRow && {
              borderBottomStyle: "solid",
              borderBottomColor: cssFns.setOpacity(uiColors.line, 0.1),
              borderBottomWidth: "2px",
            }),

          backgroundColor: isPinned
            ? uiColors.background
            : isHovered
              ? cssFns.blend(uiColors.background, uiColors.hover)
              : uiColors.background,
        }}
        aria-colindex={index}
        ref={ref}
      >
        <div
          ref={targetTooltipRef}
          css={{
            opacity: disabled || progress ? "0.5" : undefined,
          }}
        >
          <CheckboxInternal
            inputProps={mergeProps({
              ...inputProps,
              ...triggerProps,
              tabIndex,
              "aria-disabled": Boolean(disabled),
              "aria-busy": progress,
            })}
            inputRef={inputRef}
            value={value}
          />
        </div>

        {!progress &&
        typeof disabled === "string" &&
        disabled &&
        tooltipState.isOpen ? (
          <Tooltip
            state={tooltipState}
            tooltipProps={tooltipProps}
            targetRef={targetTooltipRef}
            variant="info"
          >
            {disabled}
          </Tooltip>
        ) : null}
      </td>
    );
  },
);
